import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { roundDecimals } from '@iotv/datamodel-core';
import { Milliseconds } from '@iotv/iotv-v3-types';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { Box, Card, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import { RelativeLoadingAnimation } from '../../../Popup/Loading';
import { SimpleIOModal } from '../../io/VOBbased/SimpleIOModal';
import { IOType } from '../../io/types';
import { NavSections } from '../../navigation/NavSections';
import styles from './ExpandableTable.module.css';
export const viewArray = (arr) => {
    if (arr === undefined) {
        return '';
    }
    return arr.join(',');
};
export const formatTimeDifference = (timestamp) => {
    const now = new Date();
    const diff = now.getTime() - timestamp;
    const hourDiff = Math.floor(diff / Milliseconds.HOUR);
    const minDiff = Math.floor(diff / Milliseconds.MINUTE);
    if (hourDiff < 2) {
        return `${minDiff}m's ago`;
    }
    else {
        return `${hourDiff}h's ago`;
    }
};
export const convertParentChildMapper = (mapper, parent, child, childFilter) => {
    const { type: pType, definition: pDefinition } = parent;
    const { type: cType, definition: cDefinition } = child;
    let output = [];
    const parents = mapper.getNodesOfType(pType);
    const construtPairs = parents.map((parent) => {
        let children = mapper.getChildNodes(parent).filter((childList) => childList.type === cType) || [];
        if (childFilter) {
            children = childFilter(children);
            if (children.length === 0) {
                return {};
            }
        }
        return {
            parent,
            children,
        };
    }).filter((pair) => Object.keys(pair).length !== 0);
    output = construtPairs
        .map((construct) => {
        return [construct.parent, ...construct.children];
    })
        .flat(2);
    return output;
};
const getTime = (timestamp) => {
    const date = new Date();
    const millisecondDif = date.getTime() - timestamp;
    const secondsDif = Math.round(millisecondDif / 1000);
    const minutes = Math.round(secondsDif / 60);
    if (minutes > 30) {
        date.setTime(timestamp);
        // if less than a day, show local time
        if (minutes < 60 * 24) {
            return date.toLocaleTimeString();
        }
        else {
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        }
    }
    else {
        return minutes + ' minutes';
    }
};
const getParentsAlphabetically = (list) => {
    try {
        return list.sort((a, b) => {
            return (a.parent?.name?.localeCompare(b.parent?.name ?? '', undefined, {
                numeric: true,
                sensitivity: 'base',
            }) ?? 0);
        });
    }
    catch (error) {
        console.error('Some dirty data is in parent');
        return list;
    }
};
const formatOutput = (source, attribute, redirect, related) => {
    let res = source[attribute.key];
    if (redirect && attribute.key === 'name') {
        return (_jsx(TableCell, { children: _jsx("a", { href: `/${NavSections.ADMIN_VIEW}/${source.type}/${source.id}`, target: "_blank", rel: "noreferrer", children: res }) }));
    }
    if (attribute.formatter) {
        res = attribute.formatter(res);
    }
    else if (attribute.type === 'number') {
        if (attribute.summary && related) {
            const summary = attribute.summary;
            const choosen = (related.map((real) => real[attribute.key]) || []);
            if (summary.accumlate) {
                res = choosen.reduce((partialSum, a) => partialSum + a, 0);
            }
            else if (summary.average) {
                res = choosen.reduce((partialSum, a) => partialSum + a, 0) / related.length;
                res = Math.round(res * 100) / 100; // rounding to 2 dp
            }
            else if (summary.max) {
                res = Math.max(...choosen);
            }
            else if (summary.min) {
                res = Math.min(...choosen);
            }
        }
        res = roundDecimals(res, 1);
    }
    else if (attribute.type === 'boolean') {
        res = res ? _jsx(DoneIcon, {}) : _jsx(CloseIcon, {});
    }
    else if (attribute.type === 'date') {
        res = getTime(res);
    }
    if (!res) {
        res = 'N/A';
    }
    return (_jsxs(TableCell, { align: "center", children: [res, attribute.units] }));
};
const Row = (props) => {
    const { parent, children, pDefinition, cDefinition, redirect } = props;
    const [open, setOpen] = useState(false);
    const childHeader = (relevant, definition) => {
        if (relevant.length === 0) {
            return (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: Object.keys(definition).length, children: _jsx("b", { children: "No Related Objects" }) }) }));
        }
        return (_jsx(TableRow, { children: Object.keys(definition).map((attribute) => {
                return (_jsx(TableCell, { align: "center", children: _jsx("b", { children: cDefinition[attribute]?.label }) }, attribute));
            }) }));
    };
    return (_jsxs(Fragment, { children: [_jsxs(TableRow, { sx: { '& > *': { borderBottom: 'unset' } }, onClick: () => {
                    if (children.length > 0) {
                        setOpen(!open);
                    }
                }, children: [_jsx(TableCell, { style: { width: '50px' }, children: _jsx(IconButton, { size: "small", onClick: () => {
                                if (children.length > 0) {
                                    setOpen(!open);
                                }
                            }, children: children.length > 0 ? open ? _jsx(KeyboardArrowUpIcon, {}) : _jsx(KeyboardArrowDownIcon, {}) : _jsx(NotInterestedIcon, {}) }) }), Object.keys(pDefinition).map((attribute) => {
                        return formatOutput(parent, pDefinition[attribute], redirect, children);
                    }), _jsx(TableCell, { align: "center", children: _jsx("b", { children: children.length }) })] }), _jsx(TableRow, { children: _jsx(TableCell, { style: { paddingBottom: 0, paddingTop: 0 }, colSpan: 6, children: _jsx(Collapse, { in: open, timeout: "auto", unmountOnExit: true, children: _jsx(Box, { sx: { margin: 1 }, children: _jsxs(Table, { size: "small", stickyHeader: true, children: [_jsx(TableHead, { children: childHeader(children, cDefinition) }), _jsx(TableBody, { children: children.map((child, i) => {
                                            return (_jsx(TableRow, { className: i % 2 === 0 ? styles.odd : styles.even, children: Object.keys(cDefinition).map((attribute) => {
                                                    return formatOutput(child, cDefinition[attribute], redirect);
                                                }) }, child.sk));
                                        }) })] }) }) }) }) })] }));
};
const ExpandableTable = (props) => {
    const { header = '', data, parentDefinition, childDefinition, childFilter, exportHeaders, redirect = false, elevation = 1 } = props;
    const { type: pType, definition: pDefinition } = parentDefinition;
    const { type: cType, definition: cDefinition } = childDefinition;
    if (data === undefined) {
        return _jsx(RelativeLoadingAnimation, {});
    }
    // construct pairing
    const parents = data.getNodesOfType(pType);
    const constructPairs = parents.map((parent) => {
        let children = data.getChildNodes(parent).filter((childList) => childList.type === cType) || [];
        if (childFilter) {
            children = childFilter(children);
            if (children.length === 0) {
                return {};
            }
        }
        return {
            parent,
            children,
        };
    }).filter((pair) => Object.keys(pair).length !== 0);
    return (_jsx("div", { children: _jsxs(Card, { elevation: elevation, children: [_jsx(Toolbar, { className: styles.header, children: _jsxs(Stack, { flexGrow: 1, direction: "row", justifyContent: "space-between", alignItems: "center", gap: 1, children: [_jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: header }), exportHeaders && (_jsx(SimpleIOModal, { allowIOTypeToggle: false,
                                initialIOType: IOType.EXPORT,
                                columnIndexedKeys: exportHeaders,
                                plainObjects: convertParentChildMapper(data, parentDefinition, childDefinition, childFilter),
                                setPlainObjectsInParent: () => { } }))] }) }), _jsx(TableContainer, { sx: { maxHeight: 'min(60cqh, 60vh)' }, children: _jsxs(Table, { size: "small", stickyHeader: true, children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, {}), Object.keys(pDefinition).map((attribute) => {
                                            return (_jsx(TableCell, { align: "center", children: _jsx("b", { children: pDefinition[attribute].label }) }, attribute));
                                        }), _jsx(TableCell, { align: "center", children: _jsx("b", { children: "Includes" }) })] }) }), getParentsAlphabetically(constructPairs).map((pairing) => {
                                return Row({ ...pairing, pDefinition, cDefinition, redirect });
                            })] }) })] }) }));
};
export default ExpandableTable;
